import { memo, useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import {
  FilterOption,
  FilterType
} from 'components/FilterComponents/FilterPopover/types';

import { useOfferDesignOptions } from '../../hooks/useOfferDesignOptions';
import { resolveOfferDesignSelection } from '../../OfferFilters.utils';

interface IOfferDesignFilterProps {
  offerDesignType: OfferDesignTypes;
  selected: string[];
  onSelectionChange: (ids: string[], availableIds: string[]) => void;
}

export const OfferDesignFilter = memo(
  ({ offerDesignType, selected, onSelectionChange }: IOfferDesignFilterProps) => {
    const { publisherId } = useParams();

    const { offerDesignOptions } = useOfferDesignOptions({
      publisherId: publisherId ?? '',
      offerDesignType
    });

    const options: FilterOption[] = useMemo(
      () =>
        offerDesignOptions.map(
          (o: { value: string; label: string }): FilterOption => ({
            label: o.label,
            value: o.value
          })
        ),
      [offerDesignOptions]
    );

    const availableIds = useMemo(
      () => options.map((option: FilterOption): string => option.value),
      [options]
    );

    const selectedValues = useMemo(
      () => resolveOfferDesignSelection(selected, availableIds),
      [selected, availableIds]
    );

    const handleSelectionChange = useCallback(
      (items: string[]): void => {
        onSelectionChange(items, availableIds);
      },
      [availableIds, onSelectionChange]
    );

    return (
      <FilterPopover
        label="Offer Design"
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        options={options}
        selectedItems={selectedValues}
        onSelectionChange={handleSelectionChange}
        placeholder="Search offer design"
        dataTestId="offers-offer-design-filter"
      />
    );
  }
);

OfferDesignFilter.displayName = 'OfferDesignFilter';
